<script setup lang="ts">
import { getRouteOnboardPersonalProfile } from "@/views/Onboarding/OnboardingPersonalProfile.vue";
import { getRouteOnboardPersonalQuestionnaire } from "@/views/Onboarding/OnboardingPersonalQuestionnaire.vue";
import { getRouteOnboardPersonalVerification } from "@/views/Onboarding/OnboardingPersonalVerification.vue";
import { usePersonStore } from "@/store";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { RouteNames } from "@/utils/constants.ts";

const personStore = usePersonStore();
const { person, currentStepKey } = storeToRefs(personStore);

const isAuthenticated = computed(() => person.value !== null);
const personProfile = computed(() => person.value?.profile);

// Button text based on authentication and profile status
const btnText = computed(() => {
  if (!isAuthenticated.value) return 'Start Verification';
  if (!personProfile.value) return 'Start Verification';
  if (personProfile.value.status === "IN_REVIEW") return 'Continue';
  return 'Continue';
});

// Button route - navigate to current step or first step by default
const btnRoute = computed(() => {
  // If there's a current step key, navigate to that step
  if (currentStepKey.value) {
    switch (currentStepKey.value) {
      case RouteNames.ONBOARDING_PERSON_PROFILE:
        return getRouteOnboardPersonalProfile();
      case RouteNames.ONBOARDING_PERSON_QUESTIONNAIRE:
        return getRouteOnboardPersonalQuestionnaire();
      case RouteNames.ONBOARDING_PERSON_PROFILE_VERIFY:
        return getRouteOnboardPersonalVerification();
      default:
        break;
    }
  }

  // Default to first step (Personal Profile)
  return getRouteOnboardPersonalProfile();
});
</script>

<template>
  <div class="w-full rounded-3xl shadow-xl p-6 bg-gradient-to-r from-purple-900/90 via-purple-800/80 to-amber-900/70 backdrop-blur-sm">
    <div class="flex items-center justify-between gap-6 flex-wrap md:flex-nowrap">
      <!-- Left: Icon and Text -->
      <div class="flex items-center gap-4 flex-1">
        <!-- Shield Icon with gradient background -->
        <div class="w-20 h-20 rounded-3xl bg-gradient-to-br from-pink-400 via-rose-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-lg">
          <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
          </svg>
        </div>

        <!-- Text Content -->
        <div class="flex flex-col gap-1">
          <h2 class="text-white text-xl md:text-2xl font-semibold">
            Please verify your account to access all of Moneterum
          </h2>
          <p class="text-white/80 text-sm md:text-base font-normal">
            Complete verification to unlock all features
          </p>
        </div>
      </div>

      <!-- Right: Button -->
      <div class="w-full md:w-auto">
        <button
          @click="$router.push(btnRoute)"
          class="w-full md:w-auto px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold text-lg rounded-2xl transition-all duration-200 flex items-center justify-center gap-3 shadow-xl"
        >
          {{ btnText }}
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
